// utils/notesExport.js
// Mass download catatan (Notes RAG & Notes Plain) menjadi satu arsip .zip.
// Aturan: 1 tab = 1 berkas .md. Catatan bertab dibungkus 1 folder di dalam zip.
// Tanpa dependency eksternal — penulis ZIP minimal di bawah ini memakai
// CompressionStream('deflate-raw') bila tersedia, kalau tidak jatuh ke "store".

const enc = new TextEncoder();

let CRC_TABLE = null;
function crc32(buf) {
  if (!CRC_TABLE) {
    CRC_TABLE = new Uint32Array(256);
    for (let n = 0; n < 256; n++) {
      let c = n;
      for (let k = 0; k < 8; k++) c = (c & 1) ? (0xEDB88320 ^ (c >>> 1)) : (c >>> 1);
      CRC_TABLE[n] = c >>> 0;
    }
  }
  let crc = 0xFFFFFFFF;
  for (let i = 0; i < buf.length; i++) crc = (crc >>> 8) ^ CRC_TABLE[(crc ^ buf[i]) & 0xFF];
  return (crc ^ 0xFFFFFFFF) >>> 0;
}

async function deflateRaw(bytes) {
  if (typeof CompressionStream === 'undefined') return null;
  try {
    const stream = new Blob([bytes]).stream().pipeThrough(new CompressionStream('deflate-raw'));
    const buf = await new Response(stream).arrayBuffer();
    return new Uint8Array(buf);
  } catch (_) { return null; }
}

function dosDateTime(input) {
  const d = (input instanceof Date && !isNaN(input.getTime())) ? input : new Date();
  const y = Math.max(1980, d.getFullYear());
  return {
    time: (d.getHours() << 11) | (d.getMinutes() << 5) | Math.floor(d.getSeconds() / 2),
    date: ((y - 1980) << 9) | ((d.getMonth() + 1) << 5) | d.getDate(),
  };
}

// entries: [{ name, text, date }] → Blob (application/zip)
export async function buildZipBlob(entries) {
  const parts = [];
  const central = [];
  let offset = 0;
  for (const e of entries) {
    const nameBytes = enc.encode(e.name);
    const raw = enc.encode(e.text == null ? '' : String(e.text));
    const crc = crc32(raw);
    let data = await deflateRaw(raw);
    let method = 8;
    if (!data || data.length >= raw.length) { data = raw; method = 0; }
    const { time, date } = dosDateTime(e.date);

    const local = new Uint8Array(30 + nameBytes.length);
    const lv = new DataView(local.buffer);
    lv.setUint32(0, 0x04034b50, true);
    lv.setUint16(4, 20, true);
    lv.setUint16(6, 0x0800, true);      // bit 11: nama berkas UTF-8
    lv.setUint16(8, method, true);
    lv.setUint16(10, time, true);
    lv.setUint16(12, date, true);
    lv.setUint32(14, crc, true);
    lv.setUint32(18, data.length, true);
    lv.setUint32(22, raw.length, true);
    lv.setUint16(26, nameBytes.length, true);
    lv.setUint16(28, 0, true);
    local.set(nameBytes, 30);

    const cd = new Uint8Array(46 + nameBytes.length);
    const cv = new DataView(cd.buffer);
    cv.setUint32(0, 0x02014b50, true);
    cv.setUint16(4, 20, true);
    cv.setUint16(6, 20, true);
    cv.setUint16(8, 0x0800, true);
    cv.setUint16(10, method, true);
    cv.setUint16(12, time, true);
    cv.setUint16(14, date, true);
    cv.setUint32(16, crc, true);
    cv.setUint32(20, data.length, true);
    cv.setUint32(24, raw.length, true);
    cv.setUint16(28, nameBytes.length, true);
    cv.setUint32(42, offset, true);
    cd.set(nameBytes, 46);

    parts.push(local, data);
    central.push(cd);
    offset += local.length + data.length;
  }
  const cdSize = central.reduce((a, b) => a + b.length, 0);
  const end = new Uint8Array(22);
  const ev = new DataView(end.buffer);
  ev.setUint32(0, 0x06054b50, true);
  ev.setUint16(8, central.length, true);
  ev.setUint16(10, central.length, true);
  ev.setUint32(12, cdSize, true);
  ev.setUint32(16, offset, true);
  return new Blob([...parts, ...central, end], { type: 'application/zip' });
}

function pad(n, w) { return String(n).padStart(w, '0'); }

// Nama berkas/folder aman lintas OS (Windows paling ketat).
export function safeName(s, fallback) {
  const src = String(s || '');
  const BAD = '\\/:*?"<>|';
  let base = '';
  for (let i = 0; i < src.length; i++) {
    const ch = src[i];
    base += (src.charCodeAt(i) < 32 || BAD.indexOf(ch) >= 0) ? ' ' : ch;
  }
  base = base.replace(/\s+/g, ' ').trim().replace(/[. ]+$/, '');
  const out = base.slice(0, 60).trim().replace(/[. ]+$/, '');
  return out || fallback || 'untitled';
}

function frontMatter(meta) {
  const esc = (v) => '"' + String(v == null ? '' : v).replace(/\\/g, '\\\\').replace(/"/g, '\\"') + '"';
  const lines = ['---'];
  Object.keys(meta).forEach((k) => {
    const v = meta[k];
    if (v === undefined || v === null || v === '') return;
    lines.push(k + ': ' + esc(v));
  });
  lines.push('---', '', '');
  return lines.join('\n');
}

// Satu catatan → daftar berkas .md (1 tab = 1 berkas).
export function noteToFiles(note, index) {
  const tabs = Array.isArray(note.tabs) ? note.tabs : [];
  const folder = pad(index, 3) + '-' + safeName(note.title, 'untitled');
  const meta = (tabTitle, tabIdx) => frontMatter({
    note: note.title || '',
    tab: tabTitle || '',
    scope: note.scope || 'private',
    updated_by: note.updatedBy || '',
    updated_at: note.updatedAt || '',
    created_at: note.createdAt || '',
    note_id: note.id,
    tab_index: tabIdx,
  });
  const body = (txt) => (txt == null ? '' : String(txt));

  if (!tabs.length) {
    return [{ name: folder + '.md', text: meta('', 0) + body(note.content) }];
  }
  const files = [{
    name: folder + '/00-' + safeName(note.title, 'main') + '.md',
    text: meta('', 0) + body(note.content),
  }];
  tabs.forEach((tb, i) => {
    files.push({
      name: folder + '/' + pad(i + 1, 2) + '-' + safeName(tb && tb.title, 'tab-' + (i + 1)) + '.md',
      text: meta((tb && tb.title) || ('Tab ' + (i + 1)), i + 1) + body(tb && tb.content),
    });
  });
  return files;
}

function triggerDownload(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.style.display = 'none';
  document.body.appendChild(a);
  a.click();
  setTimeout(() => { URL.revokeObjectURL(url); a.remove(); }, 2000);
}

// Mass download: daftar catatan → satu .zip, otomatis terunduh di browser.
export async function downloadNotesZip(notes, opts) {
  const o = opts || {};
  const list = Array.isArray(notes) ? notes : [];
  if (!list.length) throw new Error('No notes selected');

  const entries = [];
  const used = new Set();
  list.forEach((n, i) => {
    const d = n && n.updatedAt ? new Date(String(n.updatedAt).replace(' ', 'T')) : new Date();
    noteToFiles(n, i + 1).forEach((f) => {
      let name = f.name;
      let k = 2;
      while (used.has(name.toLowerCase())) {
        name = f.name.replace(/\.md$/, '') + '-' + (k++) + '.md';
      }
      used.add(name.toLowerCase());
      entries.push({ name, text: f.text, date: d });
    });
  });

  const blob = await buildZipBlob(entries);
  const now = new Date();
  const stamp = now.getFullYear() + pad(now.getMonth() + 1, 2) + pad(now.getDate(), 2)
    + '-' + pad(now.getHours(), 2) + pad(now.getMinutes(), 2);
  const fname = (o.prefix || 'notes') + '-' + stamp + '.zip';
  triggerDownload(blob, fname);
  return { notes: list.length, files: entries.length, name: fname, bytes: blob.size };
}

// ── IMPORT: baca .zip berisi berkas .md → daftar calon catatan ──────────────
// Kebalikan dari downloadNotesZip: folder = 1 catatan bertab, .md di root zip
// = 1 catatan tanpa tab. Front-matter YAML (kalau ada) dipakai untuk judul,
// judul tab, dan scope. ZIP dari sumber lain tetap bisa dibaca.

async function inflateRaw(bytes) {
  if (typeof DecompressionStream === 'undefined') {
    throw new Error('Browser ini tidak mendukung DecompressionStream (butuh Chrome/Edge 80+).');
  }
  const stream = new Blob([bytes]).stream().pipeThrough(new DecompressionStream('deflate-raw'));
  return new Uint8Array(await new Response(stream).arrayBuffer());
}

// File/Blob .zip → [{ name, size, text }] (hanya berkas, folder dilewati).
export async function readZipEntries(file) {
  const buf = new Uint8Array(await file.arrayBuffer());
  if (buf.length < 22) throw new Error('Berkas terlalu kecil untuk sebuah ZIP.');
  const dv = new DataView(buf.buffer, buf.byteOffset, buf.byteLength);
  let eocd = -1;
  const min = Math.max(0, buf.length - 66000);
  for (let i = buf.length - 22; i >= min; i--) {
    if (dv.getUint32(i, true) === 0x06054b50) { eocd = i; break; }
  }
  if (eocd < 0) throw new Error('Bukan berkas ZIP yang valid (End Of Central Directory tidak ditemukan).');

  const count = dv.getUint16(eocd + 10, true);
  let p = dv.getUint32(eocd + 16, true);
  const dec = new TextDecoder('utf-8');
  const out = [];

  for (let k = 0; k < count; k++) {
    if (p + 46 > buf.length || dv.getUint32(p, true) !== 0x02014b50) {
      throw new Error('Struktur ZIP rusak pada entri ke-' + (k + 1) + '.');
    }
    const flags  = dv.getUint16(p + 8, true);
    const method = dv.getUint16(p + 10, true);
    const csize  = dv.getUint32(p + 20, true);
    const usize  = dv.getUint32(p + 24, true);
    const nlen   = dv.getUint16(p + 28, true);
    const elen   = dv.getUint16(p + 30, true);
    const clen   = dv.getUint16(p + 32, true);
    const lo     = dv.getUint32(p + 42, true);
    const name   = dec.decode(buf.subarray(p + 46, p + 46 + nlen));
    p += 46 + nlen + elen + clen;

    if (!name || name.endsWith('/')) continue;
    if (flags & 1) throw new Error('ZIP terenkripsi tidak didukung: ' + name);
    if (csize === 0xFFFFFFFF || usize === 0xFFFFFFFF) throw new Error('ZIP64 belum didukung: ' + name);

    const lnlen = dv.getUint16(lo + 26, true);
    const lelen = dv.getUint16(lo + 28, true);
    const start = lo + 30 + lnlen + lelen;
    const raw = buf.subarray(start, start + csize);
    let data;
    if (method === 0) data = raw;
    else if (method === 8) data = await inflateRaw(raw);
    else throw new Error('Metode kompresi tidak didukung (' + method + ') pada ' + name);

    out.push({ name, size: usize, text: dec.decode(data) });
  }
  return out;
}

// Front-matter YAML sederhana (key: "value") di awal berkas.
export function parseFrontMatter(text) {
  let src = String(text == null ? '' : text);
  if (src.charCodeAt(0) === 0xFEFF) src = src.slice(1);
  const lines = src.split('\n');
  if ((lines[0] || '').trim() !== '---') return { meta: {}, body: src };
  let end = -1;
  for (let i = 1; i < lines.length; i++) {
    const l = lines[i].trim();
    if (l === '---' || l === '...') { end = i; break; }
  }
  if (end < 0) return { meta: {}, body: src };
  const meta = {};
  for (let i = 1; i < end; i++) {
    const line = lines[i];
    const c = line.indexOf(':');
    if (c <= 0) continue;
    const key = line.slice(0, c).trim();
    let val = line.slice(c + 1).trim();
    const q = val.charAt(0);
    if ((q === '"' || q === "'") && val.length > 1 && val.charAt(val.length - 1) === q) {
      val = val.slice(1, -1);
      if (q === '"') val = val.replace(/\\"/g, '"').replace(/\\\\/g, '\\');
    }
    if (key) meta[key] = val;
  }
  let b = end + 1;
  while (b < lines.length && lines[b].trim() === '') b++;
  return { meta, body: lines.slice(b).join('\n') };
}

function baseName(path) { return String(path || '').replace(/^.*\//, ''); }

function titleFromName(name) {
  const base = baseName(name).replace(/\.(md|markdown|txt)$/i, '');
  return base.replace(/^\d{1,4}[-_. ]+/, '').trim() || base;
}

function titleFromBody(body) {
  const m = String(body || '').match(/^[ \t]*#{1,3}[ \t]+(.+)$/m);
  return m ? m[1].replace(/#+\s*$/, '').trim() : '';
}

const MAX_TITLE = 200;
const MAX_TABS_IMPORT = 50;

function pickScope(meta) { return meta && meta.scope === 'common' ? 'common' : 'private'; }

function entryToNote(e) {
  const { meta, body } = parseFrontMatter(e.text);
  const title = String(meta.note || meta.title || titleFromBody(body) || titleFromName(e.name)).slice(0, MAX_TITLE);
  return { title, content: body, tabs: [], scope: pickScope(meta), source: e.name, bytes: e.size, files: 1 };
}

function folderToNote(dir, list) {
  const parsed = list.map((e) => {
    const fm = parseFrontMatter(e.text);
    return { e: e, meta: fm.meta, body: fm.body };
  });
  let mainIdx = parsed.findIndex(p => String(p.meta.tab_index) === '0');
  if (mainIdx < 0) mainIdx = parsed.findIndex(p => /^0+[-_. ]/.test(baseName(p.e.name)));
  if (mainIdx < 0) mainIdx = 0;
  const main = parsed[mainIdx];
  const folderName = dir.split('/').filter(Boolean).pop() || dir;
  const title = String(main.meta.note || titleFromName(folderName) || titleFromBody(main.body) || folderName).slice(0, MAX_TITLE);
  const tabs = parsed
    .filter((_, i) => i !== mainIdx)
    .map(p => ({
      title: String(p.meta.tab || titleFromBody(p.body) || titleFromName(p.e.name)).slice(0, MAX_TITLE),
      content: p.body,
    }))
    .slice(0, MAX_TABS_IMPORT);
  return {
    title, content: main.body, tabs, scope: pickScope(main.meta),
    source: dir + '/', bytes: list.reduce((a, b) => a + b.size, 0), files: list.length,
  };
}

// File .zip → daftar calon catatan (belum disimpan ke server).
export async function zipToNotes(file) {
  const all = await readZipEntries(file);
  const entries = all.filter(e => /\.(md|markdown|txt)$/i.test(e.name) && !/(^|\/)__MACOSX\//.test(e.name));
  if (!entries.length) throw new Error('ZIP tidak berisi berkas .md / .markdown / .txt.');

  const groups = new Map();
  entries.forEach((e) => {
    const i = e.name.lastIndexOf('/');
    const dir = i < 0 ? '' : e.name.slice(0, i);
    if (!groups.has(dir)) groups.set(dir, []);
    groups.get(dir).push(e);
  });

  const byName = (a, b) => a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: 'base' });
  const notes = [];
  (groups.get('') || []).slice().sort(byName).forEach(e => notes.push(entryToNote(e)));
  Array.from(groups.keys()).filter(d => d !== '').sort((a, b) => a.localeCompare(b, undefined, { numeric: true }))
    .forEach((dir) => {
      const list = groups.get(dir).slice().sort(byName);
      notes.push(list.length === 1 ? entryToNote(list[0]) : folderToNote(dir, list));
    });
  return notes;
}
